import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { getTracking, track } from "@/lib/tracking";
import { EMPTY, validate } from "@/components/form/validation";
import { StepContact, StepCenter, StepInvoice, StepPayment } from "@/components/form/Steps";

const KEY = "sl_registration";
const LABELS = ["Contatto", "Il centro", "Fattura", "Pagamento"];
const STEPS = [StepContact, StepCenter, StepInvoice, StepPayment];

const load = () => {
  try {
    return JSON.parse(sessionStorage.getItem(KEY)) || {};
  } catch {
    return {};
  }
};

const Progress = ({ step }) => (
  <div className="mb-8" data-testid="form-progress">
    <div className="grid grid-cols-4 gap-2">
      {LABELS.map((l, i) => (
        <div key={l}>
          <div className="h-1 overflow-hidden rounded-full bg-line">
            <motion.div className="h-full bg-gradient-to-r from-gold-dark via-gold to-gold-light" initial={false} animate={{ width: i < step ? "100%" : "0%" }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} />
          </div>
          <span className={`mt-2 block text-[10px] font-semibold uppercase tracking-[0.14em] sm:text-[11px] ${i < step ? "text-gold-light" : "text-sand/70"}`}>{l}</span>
        </div>
      ))}
    </div>
  </div>
);

export const RegistrationForm = () => {
  const saved = useRef(load()).current;
  const [step, setStep] = useState(saved.step || 1);
  const [data, setData] = useState({ ...EMPTY, ...(saved.data || {}) });
  const [regId, setRegId] = useState(saved.regId || null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    sessionStorage.setItem(KEY, JSON.stringify({ step, data, regId }));
  }, [step, data, regId]);

  const set = (patch) => {
    setData((d) => ({ ...d, ...patch }));
    setErrors((e) => {
      const n = { ...e };
      Object.keys(patch).forEach((k) => delete n[k]);
      return n;
    });
  };

  const toTop = () => window.__lenis?.scrollTo(formRef.current, { offset: -24, duration: 0.9 });

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(step, data);
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error("Controlla i campi evidenziati.");
      return;
    }
    setLoading(true);
    try {
      const r = await http.post("/registrations/step", { registration_id: regId, step, data, tracking: getTracking() });
      const id = r.data?.registration_id || regId;
      setRegId(id);
      if (step === 1) track("Lead");
      if (step < 4) {
        setStep(step + 1);
        if (step === 3) track("InitiateCheckout", { value: 32.94, currency: "EUR" });
        toTop();
        setLoading(false);
        return;
      }
      const c = await http.post("/payments/checkout", { registration_id: id, origin_url: window.location.origin });
      if (c.data?.checkout_url) window.location.href = c.data.checkout_url;
    } catch {
      toast.error("Si è verificato un errore di connessione. Riprova tra qualche secondo.");
      setLoading(false);
    }
  };

  const Current = STEPS[step - 1];
  const blocked = step === 2 && data.titolare === "no";

  return (
    <form ref={formRef} onSubmit={submit} noValidate className="card-lux p-6 sm:p-9" data-testid="registration-form">
      <Progress step={step} />
      <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }} data-testid={`form-step-${step}`}>
        <Current d={data} set={set} e={errors} />
      </motion.div>
      <div className="mt-9 flex items-center justify-between gap-3">
        {step > 1 ? (
          <button type="button" className="btn-ghost !px-5" onClick={() => { setStep(step - 1); setErrors({}); }} data-testid="form-back-btn">
            <ArrowLeft className="h-4 w-4" /> Indietro
          </button>
        ) : <span />}
        <button type="submit" disabled={loading || blocked} className={`btn-gold ${step === 4 ? "flex-1 sm:flex-none" : ""}`} data-testid={step === 4 ? "form-pay-btn" : "form-next-btn"}>
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {step === 4 ? "Paga €32,94 e riserva il posto" : "Avanti"}
          {!loading && step < 4 && <ArrowRight className="h-4 w-4" />}
        </button>
      </div>
    </form>
  );
};
